import React, { useState, useEffect, useRef } from 'react';
import { ethers } from 'ethers';
import '../styles/TextToVideo.css';

const TextToVideo = () => {
  const [prompt, setPrompt] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [videoUrl, setVideoUrl] = useState('');
  const [progress, setProgress] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const videoRef = useRef(null);
  const [account, setAccount] = useState('');
  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

  useEffect(() => {
    checkWalletConnection();
  }, []);

  const checkWalletConnection = async () => {
    try {
      if (window.ethereum) {
        const provider = new ethers.providers.Web3Provider(window.ethereum);
        const accounts = await provider.listAccounts();
        if (accounts.length > 0) {
          setAccount(accounts[0].address);
        }
      }
    } catch (err) {
      console.error('Error checking wallet connection:', err);
    }
  };

  const connectWallet = async () => {
    if (!window.ethereum) {
      setError('Please install MetaMask to use this feature');
      return;
    }

    try {
      const accounts = await window.ethereum.request({
        method: 'eth_requestAccounts'
      });
      
      if (accounts.length > 0) {
        setAccount(accounts[0]);
        setError('');
      }
    } catch (err) {
      console.error('Error connecting wallet:', err);
      setError('Failed to connect wallet. Please try again.');
    }
  };

  const handlePlayPause = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const generateVideo = async () => {
    if (!prompt.trim()) {
      setError('Please enter a prompt');
      return;
    }

    if (!account) {
      setError('Please connect your wallet first');
      return;
    }

    setLoading(true);
    setError('');
    setVideoUrl('');
    setProgress(0);
    setIsPlaying(false);

    const enhancedPrompt = `Cinematic masterpiece: ${prompt}. Shot on RED camera with premium lenses, cinematic lighting, and professional grade production. Scene features fluid motion, perfect exposure, and rich details. Includes atmospheric elements, volumetric lighting, and natural motion blur. Style: Hollywood cinematography with IMAX quality.`;

    const negativePrompt = 'artificial, fake, low quality, blurry, distorted, pixelated, poor lighting, amateur, unprofessional, shaky camera, bad composition, cartoon style, animation style, oversaturated, underexposed, noisy, grainy, stuttering motion, choppy movement';

    try {
      const response = await fetch('https://modelslab.com/api/v6/video/text2video', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          key: '2pZEc1AvPVnczk74RBRBsRe8RRQzsUiXqtcmUFfHD1ZVYGFZ4rja9F77srXU',
          model_id: 'cogvideox',
          prompt: enhancedPrompt,
          negative_prompt: negativePrompt,
          width: 512,
          height: 512,
          num_frames: 25,
          num_inference_steps: 50,
          guidance_scale: 15,
          fps: 12,
          motion_bucket_id: 180,
          noise_aug_strength: 0.02,
          output_type: 'gif',
          instant_response: true
        })
      });

      const data = await response.json();
      
      if (data.status === 'processing' && data.id) {
        setProgress(10);
        
        if (data.future_links && data.future_links.length > 0) {
          checkVideoAvailability(data.future_links[0]);
          startPollingProgress(data.eta || 60);
        }
      } else if (data.status === 'error') {
        throw new Error(data.message || 'Video generation failed');
      } else {
        throw new Error('Invalid response from server');
      }
    } catch (err) {
      setError(`Failed to generate video: ${err.message}`);
      setLoading(false);
    }
  };

  const checkVideoAvailability = async (url) => {
    const checkVideo = async () => {
      try {
        const response = await fetch(url);
        if (response.ok) {
          const blob = await response.blob();
          const videoUrl = URL.createObjectURL(blob);
          setVideoUrl(videoUrl);
          setLoading(false);
          return true;
        }
      } catch (err) {
        console.log('Video not ready yet...');
      }
      return false;
    };

    const interval = setInterval(async () => {
      const isReady = await checkVideo();
      if (isReady) {
        clearInterval(interval);
      }
    }, 1000);

    setTimeout(() => {
      clearInterval(interval);
      if (loading) {
        setError('Video generation timed out. Please try again.');
        setLoading(false);
      }
    }, 300000);
  };

  const startPollingProgress = (totalTime) => {
    const startTime = Date.now();
    const interval = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(95, (elapsed / (totalTime * 1000)) * 100);
      
      if (progress >= 95) {
        clearInterval(interval);
      } else {
        setProgress(Math.round(progress));
      }
    }, 1000);

    setTimeout(() => {
      clearInterval(interval);
      setProgress(100);
    }, totalTime * 1000 + 10000);
  };

  const renderVideoPlayer = () => {
    if (!videoUrl || loading) {
      return (
        <div className="video-loading">
          <div className="loading-spinner-large"></div>
          <div className="loading-text">
            Generating your video...
            <div className="progress-bar">
              <div 
                className="progress-fill" 
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="video-result">
        <video
          ref={videoRef}
          className="generated-video"
          src={videoUrl}
          onEnded={() => setIsPlaying(false)}
          loop
          playsInline
          controls={isMobile}
        />
        {!isMobile && (
          <div className="video-controls">
            <button 
              className="control-btn"
              onClick={handlePlayPause}
            >
              {isPlaying ? 'Pause' : 'Play'}
            </button>
            <a 
              href={videoUrl} 
              download="generated-video.gif"
              className="download-btn"
            >
              Download
            </a>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="text-to-video-container">
      {!account ? (
        <div className="connect-wallet-section">
          <h2>Connect Your Wallet</h2>
          <p>Please connect your wallet to access the AI Video Generator</p>
          <button onClick={connectWallet} className="connect-btn">
            Connect Wallet
          </button>
        </div>
      ) : (
        <>
          <div className="input-section">
            <h2>AI Video Generator</h2>
            <p className="beta-notice">
              🧪 Beta Feature: Results may take 1-2 minutes to generate
            </p>
            
            <div className="input-group">
              <textarea
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                placeholder="Describe your video scene in detail..."
                disabled={loading}
                maxLength={500}
              />
              <div className="char-count">{prompt.length}/500</div>
            </div>

            <button
              className="generate-btn"
              onClick={generateVideo}
              disabled={loading || !prompt.trim()}
            >
              {loading ? (
                <span className="loading-text">
                  Generating... {Math.round(progress)}%
                </span>
              ) : (
                '✨ Generate Video'
              )}
            </button>

            {error && (
              <div className="error-message">
                ❌ {error}
              </div>
            )}
          </div>

          {(videoUrl || loading) && (
            <div className="video-section">
              {renderVideoPlayer()}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default TextToVideo; 